/**
 * The one place a paywall or a limit sheet appears.
 *
 * Every monetization surface was written and mounted nowhere: `requestPaywall` ran the
 * policy, returned a variant, and each call site dropped it — so the policy said yes
 * and nothing appeared. The screens existed, their analytics existed, and no learner
 * could reach any of them.
 *
 * It is a host rather than a prop on each screen because of the invariant: one
 * `decide` function, and no screen may present a paywall directly. Mounted once at the
 * app root, that is structural rather than a rule people have to remember.
 */

import type { JSX } from 'react';
import { useApp } from '../app-context.js';
import {
  DismissalOffer,
  PaywallScreen,
  SubscriptionWelcome,
  VoiceLimitSheet,
  WinbackSheet,
} from './monetization.js';

export function MonetizationHost({
  onNavigate,
}: {
  readonly onNavigate: (route: string) => void;
}): JSX.Element | null {
  const {
    pendingPaywall,
    dismissPaywall,
    limitSheet,
    dismissLimitSheet,
    welcome,
    dismissWelcome,
    winback,
    dismissWinback,
    requestPaywall,
    dismissalOffer,
    clearDismissalOffer,
  } = useApp();

  if (pendingPaywall !== null) {
    return (
      <PaywallScreen
        variant={pendingPaywall.variant}
        trigger={pendingPaywall.trigger}
        onDismiss={dismissPaywall}
      />
    );
  }

  // What a dismissed paywall says next: the product, not the price again.
  if (dismissalOffer) {
    return (
      <DismissalOffer
        onAccept={() => {
          clearDismissalOffer();
          onNavigate('/learn/path');
        }}
        onDecline={clearDismissalOffer}
      />
    );
  }

  // The free ways round a limit, which come first and are the point of the sheet.
  if (limitSheet !== null) {
    return (
      <VoiceLimitSheet
        resetsAt={limitSheet.resetsAt}
        onNavigate={(route) => {
          dismissLimitSheet();
          onNavigate(route);
        }}
        onUpgrade={() => {
          dismissLimitSheet();
          requestPaywall('voice_limit');
        }}
        onDismiss={dismissLimitSheet}
      />
    );
  }

  // After a purchase, and only until the entitlement actually lands. Saying "we are
  // still confirming" is the honest version of a spinner on a receipt.
  if (welcome !== null) {
    return (
      <SubscriptionWelcome
        tier={welcome.tier}
        period={welcome.period}
        renewsAt={welcome.renewsAt}
        entitlementReady={welcome.entitlementReady}
        onStart={() => {
          dismissWelcome();
          onNavigate('/');
        }}
        onDismiss={dismissWelcome}
      />
    );
  }

  // A lapsed subscriber, once. The offer is real and the decline is a real answer:
  // there is no second ask in the same session.
  if (winback !== null) {
    return (
      <WinbackSheet
        lapsedDays={winback.lapsedDays}
        offer={winback.offer}
        onAccept={() => {
          dismissWinback();
          onNavigate('/me/settings');
        }}
        onDecline={dismissWinback}
      />
    );
  }

  return null;
}
